import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { LogoMark } from "@/components/Logo";

export const WA_NUMBER = "919871672654";
export const waLink = (text) => `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(text)}`;

export const WhatsAppIcon = ({ className = "h-6 w-6" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.69.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35zM12.05 21.5h-.01a9.4 9.4 0 0 1-4.79-1.31l-.34-.2-3.56.93.95-3.47-.22-.36a9.39 9.39 0 0 1-1.44-5.01c0-5.2 4.23-9.43 9.43-9.43 2.52 0 4.88.98 6.66 2.77a9.36 9.36 0 0 1 2.76 6.67c0 5.2-4.23 9.42-9.44 9.42zm8.03-17.45A11.27 11.27 0 0 0 12.05.73C5.79.73.69 5.83.69 12.09c0 2 .52 3.96 1.52 5.68L.6 23.6l5.97-1.57a11.33 11.33 0 0 0 5.47 1.39h.01c6.26 0 11.36-5.1 11.36-11.36 0-3.03-1.18-5.89-3.33-8.03z" />
  </svg>
);

const CHAT_MSG = "Hey Insane, I'd like to know more about your socks.";

export const WhatsAppChat = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-3 md:bottom-8 md:right-8">
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="whatsapp-chat-panel"
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 380, damping: 28 }}
            className="w-[min(320px,calc(100vw-2.5rem))] origin-bottom-right overflow-hidden rounded-[28px] border border-violet/40 bg-plum shadow-[0_20px_60px_rgba(0,0,0,0.5)]"
          >
            <div className="flex items-center gap-3 bg-[#25D366] px-5 py-4 text-ink">
              <LogoMark className="h-11 w-11 rounded-xl bg-ink" />
              <div className="flex-1">
                <p className="font-display text-base font-extrabold uppercase leading-none">Insane Circle</p>
                <p className="mt-1 text-xs font-semibold">Chat with us on WhatsApp</p>
              </div>
              <button data-testid="whatsapp-chat-close" onClick={() => setOpen(false)} aria-label="Close chat" className="rounded-full p-1 hover:bg-black/10">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-5">
              <div className="rounded-2xl rounded-tl-sm bg-white/10 px-4 py-3 text-sm leading-relaxed">
                Hey! Got questions about our socks or the 5 pairs for 999 Rs deal? Tap below and talk to us directly.
              </div>
              <a
                data-testid="whatsapp-chat-start"
                href={waLink(CHAT_MSG)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] py-3 font-display text-sm font-extrabold uppercase text-ink transition-transform hover:scale-[1.02]"
              >
                <WhatsAppIcon className="h-5 w-5" /> Start chat
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <motion.button
        data-testid="whatsapp-chat-toggle"
        onClick={() => setOpen((o) => !o)}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.92 }}
        aria-label="Chat on WhatsApp"
        className="relative flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-[0_10px_30px_rgba(37,211,102,0.45)]"
      >
        <span className="absolute inset-0 animate-ping rounded-full bg-[#25D366] opacity-30" />
        <WhatsAppIcon className="relative h-7 w-7" />
      </motion.button>
    </div>
  );
};
